<template>
  <button
    :class="xNs"
    :type="formSubmit ? 'submit' : 'button'"
    v-bind="{
      disabled: disabled || props.loading,
      ariadisabled: disabled || props.loading,
    }"
    @click="click"
  >
    <span :class="ns.e('content')">
      <div
        v-show="icon || loading"
        :class="[
          ns.e('icon'),
          (slots?.default || typeof text === 'string') && ns.m('mr'),
        ]"
      >
        <Loading v-if="loading" />
        <div v-else :class="ns.e('icon-wrapper')">
          <Icon v-if="typeof icon === 'string'" :name="icon" />
          <Icon v-else>
            <component :is="(icon as Component)" />
          </Icon>
        </div>
      </div>
      <slot v-if="slots?.default" />
      <span v-else-if="typeof text === 'string'">{{ text }}</span>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { computed, inject, useSlots } from 'vue'
import { throttle } from 'lodash-unified'
import { useNamespace } from '@xzx-design/hooks'
import { Icon, Loading } from '@xzx-design/components'
import { buttonEmits, buttonProps } from './button'

import type { Component } from 'vue'
import type { ProvideProps } from './button-group.vue'

defineOptions({
  name: 'XzxButton',
})

const btnTypes = ['primary', 'success', 'warning', 'danger']

const props = defineProps(buttonProps)
const emits = defineEmits(buttonEmits)

const parent = inject('XzxButtonGroup', { block: props.block }) as ProvideProps

const slots = useSlots()

const ns = useNamespace('button')

const xNs = computed(() => [
  ns.b(),
  ns.m(props.size),
  btnTypes?.includes(props?.type) && ns.m(props.type),
  props.text && ns.m('text'),
  props.plain && ns.m('plain'),
  props.dashed && ns.m('dashed'),
  (props.block || parent?.block) && ns.m('block'),
  props.round && ns.m('round'),
  props.circle && ns.m('circle'),
  props.bg && ns.m('bg'),
  ns.is('disabled', props.disabled),
  ns.is('loading', props.loading),
])

const throttleFunc = (e: Event) => !props.loading && emits('click', e)

const throttleVal: number =
  typeof props.throttle === 'boolean' ? 500 : props.throttle

const click = props?.throttle
  ? throttle(throttleFunc, throttleVal)
  : throttleFunc
</script>
